import type { FC } from 'react'
import { useEffect, useRef } from 'react'

import type {
  ICameraVideoTrack,
  ILocalAudioTrack,
  ILocalVideoTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
} from 'agora-rtc-sdk-ng'

import { isLocalTrack } from '@core/agoraRTC/libs/utils'
import { facingModeSelector } from '@core/deviceSettings'
import { useGlobalStore } from '@core/hooks/useGlobalStore'

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  videoTrack?: ILocalVideoTrack | IRemoteVideoTrack | ICameraVideoTrack
  audioTrack?: ILocalAudioTrack | IRemoteAudioTrack
  container?: HTMLElement
}

/**
 * Plays Agora audio and video tracks in a container div element.
 */
export const AgoraTrackPlayer: FC<Props> = ({
  videoTrack,
  audioTrack,
  container,
  ...rests
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const facingMode = useGlobalStore(facingModeSelector)
  // Sets mirror mode based on facing mode. Agora SDK does not automatically
  // invert the mirror mode when using front camera.
  const mirror = isLocalTrack(videoTrack) ? facingMode !== 'environment' : false
  useEffect(() => {
    if (videoTrack) {
      if (container) {
        videoTrack.play(container, { mirror })
      } else if (ref.current !== null) {
        videoTrack.play(ref.current, { mirror })
      }
      return () => {
        videoTrack.stop()
      }
    }
  }, [container, mirror, videoTrack])

  useEffect(() => {
    if (audioTrack) {
      audioTrack.play()
      return () => {
        audioTrack.stop()
      }
    }
  }, [audioTrack])

  if (container) {
    return null
  }

  // eslint-disable-next-line zeffo/no-literal-test-id
  return <div data-testid="agora-track-player" ref={ref} {...rests}></div>
}
